<template>
	<b-navbar
		v-if="$store.state.user"
		id="appbar"
		toggleable="sm"
		type="dark"
		variant="primary"
	>
		<div v-if="isLoading && isRendering" class="d-flex align-items-baseline">
			<b-navbar-brand class="px-3" to="/">
				Loading... Please wait.
				<b-spinner small />
				<span class="sr-only">Loading...</span>
			</b-navbar-brand>
		</div>
		<div v-else class="d-flex align-items-baseline">
			<b-navbar-brand class="px-3" to="/" @click="toggleProjectList">
				{{ $store.state.project ?
					$store.state.project.name :
					$t("theapplication")
				}}
				<i :class="projectListIsOpen ? 'tan-icon-arrow-up' : 'tan-icon-arrow-down'" class="text-secondary small" />
			</b-navbar-brand>
			<b-navbar-nav v-if="$store.state.project">
				<b-nav-text>
					{{ $store.state.project.location }}
				</b-nav-text>
			</b-navbar-nav>
			<b-navbar-nav v-show="isRendering">
				<b-nav-text>
					<b-icon icon="globe" animation="throb" class="ml-3" />
				</b-nav-text>
			</b-navbar-nav>
		</div>
		
		<template v-if="$store.state.user">
			<b-navbar-nav class="ml-auto">
				<b-nav-item-dropdown right no-caret>
					<template #button-content>
						<i class="tan-icon-bell-circled-filled" />
					</template>
					<notification-list />
				</b-nav-item-dropdown>
				<b-nav-item-dropdown right no-caret>
					<template #button-content>
						<i class="tan-icon-questionmark-circled-filled" />
					</template>
					<b-dropdown-item to="/support/tutorials">
						{{ $t('appbar.support-menu.tutorials') }}
					</b-dropdown-item>
					<b-dropdown-item to="/support/library">
						{{ $t('appbar.support-menu.library') }}
					</b-dropdown-item>
					<b-dropdown-item to="/support/request">
						{{ $t('appbar.support-menu.support-request') }}
					</b-dropdown-item>
					<b-dropdown-item to="/feature/request">
						{{ $t('appbar.support-menu.feature-request') }}
					</b-dropdown-item>
					<b-dropdown-item to="/apidocs">
						{{ $t('appbar.support-menu.api-documentation') }}
					</b-dropdown-item>
				</b-nav-item-dropdown>

				<b-modal 
					id="switchEnterprise"
					:title="$t('appbar.switch-enterprises.modal-title')"
					:ok-title="$t('appbar.switch-enterprises.switch')"
					@ok="savePreferredEnterprise"
				>
					<b-form-group :label="$t('appbar.switch-enterprises.dropdown-label')" label-for="enterprise-selection">
						<b-form-select id="enterprise-selection" v-model="desiredEnterprise" :options="listOfEnterprises" />
					</b-form-group>
					{{ $t('appbar.switch-enterprises.current-label', {enterprise: $store.state.user.currentEnterprise.name}) }}
				</b-modal>

				<b-modal
					id="settings"
					class="m-0"
					modal-class="settings-menu"
					content-class="settings-menu-content"
					body-class="settings-menu-body"
					footer-class="settings-menu-footer"
					:title="$t('appbar.settings-menu.title')"
					hide-backdrop
				>
					<nav class="settings-nav">
						<ul class="settings-hierarchy">
							<li v-for="section in settingsHierarchy" :key="section.id">
								{{ $t('appbar.settings-menu.'+section.id) }}
								<ul class="settings-link-list">
									<li v-for="category in section.categories" :key="category">
										<button class="settings-link" :class="{'selected-setting': selectedSetting === category}" @click="selectedSetting = category">
											{{ $t(`appbar.settings-menu.${category}.title`) }}
										</button>
									</li>
								</ul>
							</li>
						</ul>
					</nav>
					<component :is="selectedSetting+'Settings'" />
					<template #modal-footer>
						<div />
					</template>
				</b-modal>
				<b-modal
					id="filemanager"
					class="m-0"
					modal-class="settings-menu"
					content-class="settings-menu-content"
					body-class="settings-menu-body"
					footer-class="settings-menu-footer"
					:title="$t('appbar.user-menu.file-manager')"
					hide-backdrop
				>					
					<file-manager />
					<template #modal-footer>
						<div />
					</template>
				</b-modal>
				<b-nav-item-dropdown
					right
					no-caret
					@show="getFormattedEnterpriseList"
				>
					<template #button-content>
						<div class="user-menu-label">
							<i class="tan-icon-profile-circled-filled" style="float:left" />
							<div>
								{{ $store.state.user.firstName }} {{ $store.state.user.lastName }} <br>
								{{ userCurrentRole }} - {{ userCurrentEnterprise }}
							</div>
						</div>
					</template>
					<b-dropdown-item v-if="$store.state.enterpriseList.length > 1" v-b-modal.switchEnterprise>
						{{ $t('appbar.user-menu.switch-enterprise') }}
					</b-dropdown-item>
					<b-dropdown-item v-b-modal.settings>
						{{ $t('appbar.user-menu.settings-menu') }}
					</b-dropdown-item>
					<b-dropdown-item v-if="$store.state.user.currentEnterprise && $store.state.user.currentEnterprise.enterprise_user && $store.state.user.currentEnterprise.enterprise_user.role !== 'VIEWER'" v-b-modal.filemanager>
						{{ $t('appbar.user-menu.file-manager') }}
					</b-dropdown-item>
					<b-dropdown-item @click="signOut()">
						{{ $t('appbar.user-menu.logout') }}
					</b-dropdown-item>
				</b-nav-item-dropdown>
			</b-navbar-nav>
			<!--			<b-nav-text class="text-white text-capitalize px-3" /> -->
		</template>
	</b-navbar>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import FileManager from '@/views/files/FileManager.vue';
import NotificationList from './NotificationList.vue';
import CompanyInformationSettings from './CompanyInformationSettings.vue';
import ContactsSettings from './ContactsSettings.vue';
import ManageProjectsSettings from './ManageProjectsSettings.vue';
import ManageUsersSettings from './ManageUsersSettings.vue';
import UserInformationSettings from './UserInformationSettings.vue';
import SecuritySettings from './SecuritySettings.vue';

export default {
	name: 'AppBar',
	components: {
		CompanyInformationSettings,
		ContactsSettings,
		FileManager,
		ManageProjectsSettings,
		ManageUsersSettings,
		NotificationList,
		SecuritySettings,
		UserInformationSettings,
	},
	data() {
		return {
			selectedSetting: 'ManageProjects',
			settingsHierarchy: [
				{
					categories: ['CompanyInformation', 'Contacts', 'ManageUsers'],
					id: 'company',
				},
				{
					categories: ['ManageProjects'],
					id: 'projects',
				},
				{
					categories: ['UserInformation', 'Security'],
					id: 'yourPreferences',
				},
			],
			listOfEnterprises: [],
			desiredEnterprise: null,
		};
	},
	computed: {
		...mapState(['projectListIsOpen', 'isLoading', 'isRendering']),
		userCurrentRole() {
			return this.$store.state.user.currentEnterprise.enterprise_user.role;
		},
		userCurrentEnterprise() {
			return this.$store.state.user.currentEnterprise.name || 'No Enterprise';
		},
	},
	created() {
		if (!this.$store.state.user.currentEnterprise.name) {
			console.error('No Enterprises for Current User');
			this.$bvToast.toast(this.$t('no-enterprises'), {
				title: this.$t('toast.error'),
				autoHideDelay: 10000,
				variant: 'danger',
			});
		}
	},
	methods: {
		...mapMutations(['setProjectListIsOpen', 'setLoading', 'setRendering', 'closeAllPanels']),
		async getFormattedEnterpriseList() {
			this.$store.dispatch('fetchUserEnterprises').then(() => {
				this.listOfEnterprises = [];
				for (const e of this.$store.state.enterpriseList) {
					this.listOfEnterprises.push({ text: e.name, value: e.id });
				}
			});
		},
		savePreferredEnterprise() {
			this.$store.dispatch('savePreferredEnterprise', this.desiredEnterprise).then(() => {
				this.$store.dispatch('refreshUserToken').then(() => {
					this.$store.dispatch('fetchProjectList');
				});
			});
		},
		signOut() {
			this.$store.commit('setUser', null);
			this.setLoading(false);
			this.setRendering(false);
			this.closeAllPanels();
			this.$store.state.scenario = null;
			this.$store.state.project = null;
			this.$router.replace('/signin');
		},
		toggleProjectList() {
			this.setProjectListIsOpen(!this.projectListIsOpen);
		},
	},
};
</script>

<style lang="scss">
@import '@/assets/variables.scss';
#appbar {
	border-bottom: 1px solid $body-color;

	.nav-link i {
		color: $body-color;
	}

	.dropdown {
		&.show > .dropdown-toggle > i {
			color: rgba(255, 255, 255, 0.5);
		}
		.list-group-item {
			background: transparent;
		}
		.dropdown-menu {
			margin-top: 1px;
		}
	}

	.navbar-nav > .nav-item {
		border-left: 1px solid $body-color;

		&:last-child {
			border-right: 1px solid $body-color;
		}
		& > .nav-link {
			padding: 0.1rem !important;
			font-size: 1.7rem;
		}
	}
}

.settings-menu {
	overflow: hidden !important;
	padding: 0% !important;
}

.settings-menu .modal-dialog {
	position: absolute;
	bottom: 0;
	margin: 0;
}

.settings-menu-content {
	border: none !important;
	height: calc(100vh - 44px);
	width: 100vw !important;
}

.settings-menu-body {
	display: flex;
	background: #eeeeee;
	height: 100%;
	padding: 0 !important;
}

.settings-nav {
	background: #dedddd;
	flex-basis: 300px;
	overflow-y: auto;
	padding: 1rem;
}

.settings-hierarchy {
	font-weight: bold;
	list-style: none;
	padding: 0;
}

.settings-link-list {
	list-style: none;
	padding: 0;
}

.settings-link {
	border: none;
	background: none;
	padding-left: 0.75rem;
	text-align: left;
	width: 100%;
}

.selected-setting {
	background: #cccccc;
	font-weight: bold;
}

.settings-menu-footer {
	border: none !important;
	height: 0;
}

.user-menu-label {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	> div {
		font-size: 0.75rem;
		line-height: 1.2em;
		padding: 0.3rem 0.1rem 0.3rem 35px;
		margin-left: -30px;
	}
}
</style>
